import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/features/auth/hooks/useAuth";
import { leaveService } from "@/features/leave/services/leaveService";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Button } from "@/shared/components/ui/Button";
import { Input } from "@/shared/components/ui/Input";
import { CompanySearchSelect } from "@/shared/components/ui/CompanySearchSelect";
import { Toast } from "@/shared/components/feedback/Toast";
import type { LeaveField, LeaveTemplateSummary } from "@/features/leave/types/leave";

const OFFICER_TYPES = ["officer", "admin"];
const FIELD_TYPES: LeaveField["type"][] = ["text", "textarea", "date", "number", "dropdown", "file"];

const generateFieldId = () => `f_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

type BuilderView = "list" | "edit";

export const LeaveFormBuilderPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userData } = useAuth();
  const isOfficer = OFFICER_TYPES.includes(userData?.userType?.toLowerCase() || "");

  // Company selection (Step 1 for officers)
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedCompanyLabel, setSelectedCompanyLabel] = useState("");

  // View state: list of templates or the editor
  const [view, setView] = useState<BuilderView>("list");
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);

  // Templates list
  const [templates, setTemplates] = useState<LeaveTemplateSummary[]>([]);
  const [listLoading, setListLoading] = useState(false);

  // Template editor state
  const [fields, setFields] = useState<LeaveField[]>([]);
  const [templateTitle, setTemplateTitle] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [templateCategory, setTemplateCategory] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [notificationEmails, setNotificationEmails] = useState("");
  const [templateLoading, setTemplateLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [error, setError] = useState("");

  // Copy-to-company state
  const [copyTemplateId, setCopyTemplateId] = useState<string | null>(null);
  const [copyTargetCompany, setCopyTargetCompany] = useState("");
  const [copyTargetLabel, setCopyTargetLabel] = useState("");
  const [copying, setCopying] = useState(false);
  const [showSavedBanner, setShowSavedBanner] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const showToast = useCallback((message: string, type: "success" | "error") => {
    if (type === "success") {
      setShowSavedBanner(true);
      setTimeout(() => setShowSavedBanner(false), 3000);
    } else {
      setToast({ message, type });
    }
  }, []);

  // ── Load template list ──────────────────────────────────────────────────

  const loadTemplateList = useCallback(async (companyId?: string) => {
    try {
      setListLoading(true);
      const data = await leaveService.getTemplates(companyId);
      setTemplates(data);
    } catch (err) {
      console.error("Failed to load templates list:", err);
      setTemplates([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  // Auto-load for non-officers
  useEffect(() => {
    if (!isOfficer) {
      loadTemplateList();
    }
  }, [isOfficer, loadTemplateList]);

  // ── Company selection (officers) ────────────────────────────────────────

  const handleCompanySelect = (companyId: string, label: string) => {
    setSelectedCompany(companyId);
    setSelectedCompanyLabel(label);
    setView("list");
    setTemplates([]);
    if (companyId) {
      loadTemplateList(companyId);
    }
  };

  // ── Open editor: create new or edit existing ────────────────────────────

  const openCreateNew = () => {
    setEditingTemplateId(null);
    setFields([]);
    setTemplateTitle("");
    setTemplateDescription("");
    setTemplateCategory("");
    setIsPublished(false);
    setNotificationEmails("");
    setError("");
    setView("edit");
  };

  const openEditTemplate = async (templateId: string) => {
    try {
      setTemplateLoading(true);
      setError("");
      const template = await leaveService.getTemplate(
        isOfficer ? selectedCompany : undefined,
        templateId,
      );
      setEditingTemplateId(templateId);
      setFields(template.fields || []);
      setTemplateTitle(template.title || "");
      setTemplateDescription(template.description || "");
      setTemplateCategory(template.category || "");
      setIsPublished(template.is_published ?? false);
      setNotificationEmails((template.config?.notificationEmails || []).join(", "));
      setView("edit");
    } catch (err) {
      console.error("Failed to load template for editing:", err);
      showToast(t("feed.form.save_error"), "error");
    } finally {
      setTemplateLoading(false);
    }
  };

  // ── Field manipulation ──────────────────────────────────────────────────

  const addField = () => {
    setFields((prev) => [
      ...prev,
      { id: generateFieldId(), label: "", type: "text", required: false },
    ]);
  };

  const updateField = (index: number, updates: Partial<LeaveField>) => {
    setFields((prev) =>
      prev.map((f, i) => (i === index ? { ...f, ...updates } : f)),
    );
  };

  const removeField = (index: number) => {
    setFields((prev) => prev.filter((_, i) => i !== index));
  };

  const moveField = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= fields.length) return;
    const updated = [...fields];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setFields(updated);
  };

  // ── Save ────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setError("");

    if (isOfficer && !selectedCompany) {
      setError(t("leave.error_select_company"));
      return;
    }

    if (!templateTitle.trim()) {
      setError(t("leave.error_template_title"));
      return;
    }

    if (fields.length === 0) {
      setError(t("leave.error_no_fields"));
      return;
    }

    const invalidFields = fields.filter((f) => !f.label.trim());
    if (invalidFields.length > 0) {
      setError(t("leave.error_field_label"));
      return;
    }

    if (!notificationEmails.trim()) {
      setError(t("leave.error_notification_emails"));
      return;
    }

    const emails = notificationEmails
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);

    setSaving(true);
    try {
      await leaveService.saveTemplate(
        { notificationEmails: emails },
        fields.map((f) => ({
          ...f,
          options: f.type === "dropdown" ? f.options : undefined,
        })),
        isOfficer ? selectedCompany : undefined,
        templateTitle,
        templateDescription,
        editingTemplateId ?? undefined,
        templateCategory || undefined,
        isPublished,
      );
      showToast(t("leave.template_saved"), "success");
      // Refresh list and return to list view
      await loadTemplateList(isOfficer ? selectedCompany : undefined);
      setView("list");
    } catch (err: any) {
      console.error("Failed to save template:", err);
      const errMsg = err?.response?.data?.error || t("feed.form.save_error");
      setError(errMsg);
      showToast(errMsg, "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────

  const handleDelete = async (templateId: string) => {
    setDeletingId(templateId);
    try {
      await leaveService.deleteTemplate(templateId);
      setTemplates((prev) => prev.filter((t) => t.template_id !== templateId));
      showToast(t("leave.template_deleted"), "success");
    } catch (err: any) {
      const errMsg = err?.response?.data?.error || t("feed.form.save_error");
      showToast(errMsg, "error");
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  // ── Copy to Company ─────────────────────────────────────────────────────

  const openCopyModal = (templateId: string) => {
    setCopyTemplateId(templateId);
    setCopyTargetCompany("");
    setCopyTargetLabel("");
  };

  const handleCopyTemplate = async () => {
    if (!copyTemplateId || !copyTargetCompany) return;
    setCopying(true);
    try {
      const source = await leaveService.getTemplate(
        isOfficer ? selectedCompany : undefined,
        copyTemplateId,
      );
      const copied = await leaveService.saveTemplate(
        source.config || { notificationEmails: [] },
        source.fields || [],
        copyTargetCompany,
        source.title,
        source.description,
        undefined, // no templateId → creates new
        source.category,
        false, // always save copy as draft
      );
      setCopyTemplateId(null);

      // Switch to the target company and open the copied form in edit view
      setSelectedCompany(copyTargetCompany);
      setSelectedCompanyLabel(copyTargetLabel);
      setEditingTemplateId(copied.template_id);
      setFields(copied.fields || []);
      setTemplateTitle(copied.title || "");
      setTemplateDescription(copied.description || "");
      setTemplateCategory(copied.category || "");
      setIsPublished(copied.is_published ?? false);
      setNotificationEmails((copied.config?.notificationEmails || []).join(", "));
      setError("");
      setView("edit");
    } catch (err: any) {
      const errMsg = err?.response?.data?.error || t("feed.form.save_error");
      showToast(errMsg, "error");
    } finally {
      setCopying(false);
    }
  };

  // ── Render: List View ───────────────────────────────────────────────────

  const renderListView = () => {
    const showList = isOfficer ? !!selectedCompany : true;

    return (
      <div>
        {/* Company Selection (officers) */}
        {isOfficer && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6 mb-4">
            <CompanySearchSelect
              value={selectedCompany}
              onChange={handleCompanySelect}
              label={t("leave.select_company")}
              required
              hint={t("leave.builder_company_hint")}
              feature="company_form"
            />
          </div>
        )}

        {/* Prompt to select company */}
        {isOfficer && !selectedCompany && (
          <div className="text-center py-12 text-sm text-gray-400 bg-white rounded-xl border-2 border-dashed border-gray-200">
            <svg className="w-10 h-10 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            {t("leave.company_prompt_builder")}
          </div>
        )}

        {/* Templates list */}
        {showList && (
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-800">
                {t("leave.form_templates")} {!listLoading && `(${templates.length})`}
              </h2>
              <Button size="sm" onClick={openCreateNew}>
                {t("leave.create_new_form")}
              </Button>
            </div>

            {listLoading ? (
              <div className="py-10"><Spinner /></div>
            ) : templates.length === 0 ? (
              <div className="text-center py-12 text-sm text-gray-400">
                <svg className="w-10 h-10 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t("leave.no_templates_yet")}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {templates.map((tpl) => (
                  <li key={tpl.template_id} className="flex items-center gap-3 px-4 md:px-6 py-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{tpl.title || t("leave.untitled_form")}</p>
                      {tpl.description && (
                        <p className="text-xs text-gray-400 truncate mt-0.5">{tpl.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-1">
                        {tpl.category && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-600">
                            {tpl.category}
                          </span>
                        )}
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${tpl.is_published ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                          {tpl.is_published ? t("leave.published") : t("leave.draft")}
                        </span>
                        <span className="text-[10px] text-gray-400">v{Number(tpl.version).toFixed(1)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="secondary" onClick={() => openEditTemplate(tpl.template_id)}>
                        {t("leave.edit")}
                      </Button>
                      {isOfficer && (
                        <button
                          type="button"
                          onClick={() => openCopyModal(tpl.template_id)}
                          className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title={t("leave.copy_to_company")}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      )}
                      {confirmDeleteId === tpl.template_id ? (
                        <>
                          <Button
                            size="sm"
                            variant="danger"
                            loading={deletingId === tpl.template_id}
                            onClick={() => handleDelete(tpl.template_id)}
                          >
                            {t("leave.confirm_delete")}
                          </Button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteId(null)}
                            className="text-xs text-gray-400 hover:text-gray-600 px-1"
                          >
                            {t("leave.cancel")}
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(tpl.template_id)}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title={t("leave.delete_form")}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Render: Edit View ───────────────────────────────────────────────────

  const renderEditView = () => (
    <div className="max-w-3xl mx-auto">
      {templateLoading ? (
        <Spinner />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6 space-y-6">
          {/* Template Title */}
          <Input
            label={t("leave.template_title")}
            placeholder={t("leave.template_title_placeholder")}
            value={templateTitle}
            onChange={(e) => setTemplateTitle(e.target.value)}
          />

          {/* Template Description */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              {t("leave.template_description")}
            </label>
            <textarea
              rows={3}
              placeholder={t("leave.template_description_placeholder")}
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              className="w-full px-3 py-2.5 md:py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Category + Published row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t("leave.template_category")}
              placeholder={t("leave.template_category_placeholder")}
              value={templateCategory}
              onChange={(e) => setTemplateCategory(e.target.value)}
            />
            <div className="flex items-center gap-2 pt-6">
              <input
                id="is_published"
                type="checkbox"
                checked={isPublished}
                onChange={(e) => setIsPublished(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <label htmlFor="is_published" className="text-sm font-medium text-gray-700 cursor-pointer">
                {t("leave.publish_form")}
              </label>
            </div>
          </div>

          {/* Notification Emails */}
          <div>
            <Input
              label={t("leave.notification_emails")}
              placeholder={t("leave.notification_emails_placeholder")}
              value={notificationEmails}
              onChange={(e) => setNotificationEmails(e.target.value)}
            />
            <p className="text-xs text-gray-400 mt-1">
              {t("leave.notification_emails_hint")}
            </p>
          </div>

          {/* Fields List */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-800">
                {t("leave.form_fields")} ({fields.length})
              </h2>
              <Button size="sm" onClick={addField}>
                {t("leave.add_field")}
              </Button>
            </div>

            {fields.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                {t("leave.no_fields")}
              </div>
            ) : (
              <div className="space-y-3">
                {fields.map((field, index) => (
                  <div key={field.id} className="border border-gray-200 rounded-lg p-3 md:p-4 bg-gray-50">
                    <div className="flex items-start gap-3">
                      {/* Reorder buttons */}
                      <div className="flex flex-col gap-1 pt-1">
                        <button type="button" onClick={() => moveField(index, -1)} disabled={index === 0} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        </button>
                        <button type="button" onClick={() => moveField(index, 1)} disabled={index === fields.length - 1} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>

                      {/* Field config */}
                      <div className="flex-1 space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <Input
                            label={t("leave.field_label")}
                            placeholder={t("leave.field_label_placeholder")}
                            value={field.label}
                            onChange={(e) => updateField(index, { label: e.target.value })}
                          />
                          <div className="space-y-1">
                            <label className="block text-sm font-medium text-gray-700">
                              {t("leave.field_type")}
                            </label>
                            <select
                              value={field.type}
                              onChange={(e) =>
                                updateField(index, {
                                  type: e.target.value as LeaveField["type"],
                                  options: e.target.value === "dropdown" ? [""] : undefined,
                                })
                              }
                              className="w-full px-3 py-2.5 md:py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                              {FIELD_TYPES.map((ft) => (
                                <option key={ft} value={ft}>{t(`leave.field_type_${ft}`)}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Dropdown options */}
                        {field.type === "dropdown" && (
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-600">
                              {t("leave.field_options")}
                            </label>
                            {(field.options || [""]).map((opt, optIdx) => (
                              <div key={optIdx} className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={opt}
                                  placeholder={`Option ${optIdx + 1}`}
                                  onChange={(e) => {
                                    const newOpts = [...(field.options || [])];
                                    newOpts[optIdx] = e.target.value;
                                    updateField(index, { options: newOpts });
                                  }}
                                  className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newOpts = (field.options || []).filter((_, i) => i !== optIdx);
                                    updateField(index, { options: newOpts.length ? newOpts : [""] });
                                  }}
                                  className="p-1 text-gray-400 hover:text-red-500"
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => updateField(index, { options: [...(field.options || []), ""] })}
                              className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                            >
                              {t("leave.field_add_option")}
                            </button>
                          </div>
                        )}

                        {/* Required toggle */}
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={field.required || false}
                            onChange={(e) => updateField(index, { required: e.target.checked })}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          {t("leave.field_required")}
                        </label>

                        <p className="text-[11px] text-gray-400">ID: {field.id}</p>
                      </div>

                      {/* Remove button */}
                      <button
                        type="button"
                        onClick={() => removeField(index)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Field button at bottom for easy access without scrolling up */}
            {fields.length > 0 && (
              <div className="mt-3 flex justify-end">
                <Button size="sm" onClick={addField}>
                  {t("leave.add_field")}
                </Button>
              </div>
            )}
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );

  // ── Root render ─────────────────────────────────────────────────────────

  return (
    <div>
      {/* Floating saved banner */}
      {showSavedBanner && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl shadow-lg text-sm font-medium pointer-events-none">
          <svg
            className="w-4 h-4 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          {t("leave.template_saved")}
        </div>
      )}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Copy to Company Modal */}
      {copyTemplateId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
            <h2 className="text-base font-semibold text-gray-900">{t("leave.copy_to_company")}</h2>
            <p className="text-sm text-gray-500">{t("leave.copy_to_company_hint")}</p>
            <CompanySearchSelect
              value={copyTargetCompany}
              onChange={(id, label) => {
                setCopyTargetCompany(id);
                setCopyTargetLabel(label);
              }}
              label={t("leave.select_company")}
              required
              feature="company_form"
            />
            {copyTargetCompany && copyTargetCompany === selectedCompany && (
              <p className="text-xs text-amber-600">{t("leave.copy_same_company_warning")}</p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setCopyTemplateId(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-lg"
              >
                {t("leave.cancel")}
              </button>
              <Button
                onClick={handleCopyTemplate}
                loading={copying}
                disabled={!copyTargetCompany}
              >
                {t("leave.copy_confirm")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-gray-50 -mx-3 px-3 md:-mx-6 md:px-6 py-3 mb-4 md:mb-6 border-b border-gray-200">
        <button
          type="button"
          onClick={() => {
            if (view === "edit") {
              setView("list");
            } else {
              navigate("/leave");
            }
          }}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 active:text-gray-800 p-2 -ml-2 rounded-lg mb-1"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {view === "edit" ? t("leave.form_builder") : t("leave.title")}
        </button>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg md:text-xl font-bold text-gray-900 truncate">
              {view === "edit"
                ? editingTemplateId
                  ? t("leave.edit_form")
                  : t("leave.create_new_form")
                : t("leave.form_builder")}
            </h1>
            {selectedCompanyLabel && (
              <p className="text-xs text-gray-500 truncate">
                {t("leave.form_builder_subtitle_for", { company: selectedCompanyLabel })}
              </p>
            )}
          </div>
          {view === "edit" && !templateLoading && (
            <Button onClick={handleSave} loading={saving} className="shrink-0">
              {t("leave.save_template")}
            </Button>
          )}
        </div>
      </div>

      {view === "list" ? renderListView() : renderEditView()}
    </div>
  );
};
